/**
 * Pickup helpers — derive the earliest departure time and pickup-point count
 * from a trip's `pickup_options[]` array.
 *
 * Each pickup option carries a `time` string in HH:MM format. We surface the
 * EARLIEST one on cards + info rows so members can see "the trip leaves at
 * X:XX" at a glance without having to open the pickup accordion. Falls back
 * to the trip-level `start_time` for events that don't use pickup points.
 */

const HHMM = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/** Normalise 'H:MM' → 'HH:MM'. Returns '' when input is missing/invalid. */
const normaliseTime = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  const m = HHMM.exec(s);
  if (!m) return '';
  return `${m[1].padStart(2, '0')}:${m[2]}`;
};

/** Return every pickup `time` value that parses cleanly. */
const collectTimes = (trip) => {
  const out = [];
  for (const p of (trip?.pickup_options || [])) {
    const t = normaliseTime(p?.time || p?.pickup_time);
    if (t) out.push(t);
  }
  return out;
};

/**
 * Earliest departure time as 'HH:MM', or '' when the trip has neither
 * pickup times nor a trip-level start_time. Result is stable across
 * re-renders and safe to embed in test IDs / calendar URLs.
 */
export const earliestDepartureTime = (trip) => {
  const times = collectTimes(trip);
  if (times.length > 0) return times.slice().sort()[0];
  return normaliseTime(trip?.start_time);
};

/** True when the trip has multiple pickup points with times set. */
export const hasMultiplePickups = (trip) => {
  return (trip?.pickup_options || []).filter((p) => normaliseTime(p?.time)).length > 1;
};

/** Number of pickup points (with times) — for the "· 2 σημεία" pill hint. */
export const pickupPointCount = (trip) => {
  return (trip?.pickup_options || []).filter((p) => normaliseTime(p?.time)).length;
};
